/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { Modal, Button, Form, Alert, Spinner, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import { AuthContext } from "../../../contexts/AuthContext";

import {
  FaHandHoldingHeart,
  FaMoneyBillWave,
  FaFileAlt,
  FaShieldAlt,
  FaHeart,
  FaHospital,
  FaRing,
  FaBaby,
  FaExclamationTriangle,
} from "react-icons/fa";
import UserSelector from "../Cards/UserSelector";

// Add custom styles for the modal
const modalStyles = `
  .modal-lg {
    max-width: 900px !important;
  }
  
  .user-selector-modal .modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
  
  .user-selector-container {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background: #f8f9fa;
    max-height: 300px;
    overflow-y: auto;
  }
  
  .benefit-types-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 10px;
  }
  
  .benefit-type-item {
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .benefit-type-item:hover {
    background: #f8f9fa;
    border-color: #28a745;
  }
  
  .benefit-type-item.selected {
    background: #d4edda;
    border-color: #28a745;
  }
  
  .compact-user-selector .users-section {
    max-height: 250px;
  }

  .dependent-badge {
    background: #e9ecef;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.85rem;
  }
`;

const AddBenefitModal = ({ show, onHide, token, adminId, adminName }) => {
  const { user } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    user_id: "",
    dependent_id: "",
    type: "FUNERAL",
    amount: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDependent, setSelectedDependent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Benefit types with icons and descriptions
  const benefitTypes = [
    {
      value: "FUNERAL",
      label: "Funeral Support",
      icon: <FaHeart className="me-2" />,
      color: "danger",
      description: "Financial support for funeral expenses",
    },
    {
      value: "MEDICAL",
      label: "Medical Support",
      icon: <FaHospital className="me-2" />,
      color: "primary",
      description: "Support for medical bills and healthcare",
    },
    {
      value: "MARRIAGE",
      label: "Marriage Support",
      icon: <FaRing className="me-2" />,
      color: "success",
      description: "Financial assistance for marriage ceremonies",
    },
    {
      value: "BIRTH",
      label: "Birth/Child Support",
      icon: <FaBaby className="me-2" />,
      color: "info",
      description: "Support for childbirth and child welfare",
    },
    {
      value: "EMERGENCY",
      label: "Emergency Support",
      icon: <FaExclamationTriangle className="me-2" />,
      color: "warning",
      description: "Urgent financial assistance",
    },
    {
      value: "OTHER",
      label: "Other Support",
      icon: <FaHandHoldingHeart className="me-2" />,
      color: "secondary",
      description: "Other types of welfare support",
    },
  ];

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      resetForm();
    }
  }, [show]);

  const resetForm = () => {
    setFormData({
      user_id: "",
      dependent_id: "",
      type: "FUNERAL",
      amount: "",
      description: "",
    });
    setSelectedUser(null);
    setSelectedDependent(null);
    setError("");
    setIsSubmitting(false);
  };

  const handleUserSelect = (user) => {
    console.log("User selected in modal:", user);
    setSelectedUser(user);
    setFormData(prev => ({
      ...prev,
      user_id: user?.user_id || "",
      dependent_id: "" // Reset dependent when user changes
    }));
    setSelectedDependent(null);
  };

  const handleDependentSelect = (dependent) => {
    console.log("Dependent selected in modal:", dependent);

    if (!dependent) {
      setSelectedDependent(null);
      setFormData(prev => ({
        ...prev,
        dependent_id: ""
      }));
      return;
    }

    setSelectedDependent(dependent);
    setFormData(prev => ({
      ...prev,
      dependent_id: dependent.id
    }));

    // Show a brief toast notification
    toast.info(`Selected: ${dependent.full_name || dependent.name}`, {
      autoClose: 2000,
      hideProgressBar: true,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError("");
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow only numbers and decimal point
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        amount: value,
      }));
    }
  };

  const addBenefit = async () => {
    try {
      const response = await fetch(
        `https://aceswafund.org/welfare/welfare-system/admin-add-benefit`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: parseInt(formData.user_id),
            dependent_id: formData.dependent_id ? parseInt(formData.dependent_id) : null,
            type: formData.type,
            amount: parseFloat(formData.amount),
            description: formData.description.trim(),
            admin_id: adminId || user?.id,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error in addBenefit:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) return;

    // Validation
    if (!formData.user_id) {
      setError("Please select a user");
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError("Please enter a valid amount greater than 0");
      return;
    }

    if (parseFloat(formData.amount) > 100000) {
      setError("Amount cannot exceed GHS 100,000");
      return;
    }

    if (!formData.description || formData.description.trim().length < 10) {
      setError("Please provide a detailed description (at least 10 characters)");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    setError("");

    try {
      const result = await addBenefit();

      if (result.status) {
        toast.success("✅ Benefit added successfully!");

        // Show SMS notification if sent
        if (result.sms) {
          const smsStatus = result.sms;
          if (smsStatus.member_sent || smsStatus.admin_sent) {
            toast.info(
              `📱 SMS sent to ${smsStatus.member_sent ? "member" : ""}${smsStatus.admin_sent ? " and admin" : ""
              }`
            );
          }
        }

        // Show summary of what was added
        toast.info(
          <div>
            <strong>Benefit Summary:</strong><br />
            Type: {formData.type}<br />
            Amount: ₵{parseFloat(formData.amount).toFixed(2)}<br />
            For: {selectedUser?.full_name}
            {selectedDependent && ` → ${selectedDependent.full_name}`}
          </div>,
          { autoClose: 5000 }
        );

        resetForm();
        onHide();
      } else {
        setError(result.message || "Failed to add benefit");
        toast.error("❌ " + (result.message || "Failed to add benefit"));
      }
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
      toast.error("❌ " + (err.message || "Network error. Please check your connection."));
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const getSelectedBenefitType = () => {
    return (
      benefitTypes.find((type) => type.value === formData.type) ||
      benefitTypes[0]
    );
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    if (!amount) return "0.00";
    return parseFloat(amount).toFixed(2);
  };

  return (
    <>
      <style>{modalStyles}</style>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="lg"
        backdrop="static"
        dialogClassName="user-selector-modal"
        contentClassName="border-0 shadow"
      >
        <Modal.Header closeButton className="bg-success text-white py-3">
          <Modal.Title className="d-flex align-items-center">
            <FaHandHoldingHeart className="me-2" size={24} />
            <span>Add Benefit</span>
            <small className="ms-3 opacity-75">
              <FaShieldAlt className="me-1" />
              {adminName || user?.full_name || "Admin"}
            </small>
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className="p-4" style={{ maxHeight: "80vh", minWidth: "800px", overflowY: "auto" }}>
            {error && (
              <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4">
                <strong>Error:</strong> {error}
              </Alert>
            )}

            {/* User Selector Component */}
            <div className="mb-4">
              <Form.Label className="fw-bold fs-6 mb-3">
                <span className="bg-success text-white px-3 py-1 rounded">Step 1</span>
                <span className="ms-2">Select User and Dependent</span>
              </Form.Label>
              <div className="border rounded p-3 bg-light">
                <UserSelector
                  onSelectUser={handleUserSelect}
                  onSelectDependent={handleDependentSelect}
                  selectedUserId={selectedUser?.user_id}
                  selectedDependentId={selectedDependent?.id}
                />
              </div>
            </div>

            {/* Selected User/Dependent Preview */}
            {selectedUser && (
              <Alert variant="success" className="mb-4 py-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <strong className="me-2">Selected:</strong>
                    <span className="fw-bold">{selectedUser.full_name}</span>
                    {selectedUser.email && (
                      <small className="text-muted ms-2">({selectedUser.email})</small>
                    )}
                  </div>
                  {selectedDependent && (
                    <div className="dependent-badge">
                      <span className="fw-bold text-success">Dependent:</span>
                      <span className="ms-1">{selectedDependent.full_name}</span>
                      {selectedDependent.relationship && (
                        <span className="text-muted ms-1">
                          ({selectedDependent.relationship})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </Alert>
            )}

            {/* Benefit Type Selection */}
            <div className="mb-4">
              <Form.Label className="fw-bold fs-6 mb-3">
                <span className="bg-success text-white px-3 py-1 rounded">Step 2</span>
                <span className="ms-2">Select Benefit Type</span>
              </Form.Label>
              <Row xs={1} md={2} lg={3} className="g-3">
                {benefitTypes.map((benefit) => (
                  <Col key={benefit.value}>
                    <div
                      className={`benefit-type-item ${formData.type === benefit.value ? 'selected' : ''}`}
                      onClick={() => handleInputChange({ target: { name: 'type', value: benefit.value } })}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleInputChange({ target: { name: 'type', value: benefit.value } });
                        }
                      }}
                    >
                      <Form.Check
                        type="radio"
                        id={`type-${benefit.value}`}
                        name="type"
                        value={benefit.value}
                        checked={formData.type === benefit.value}
                        onChange={() => { }}
                        label={
                          <div>
                            <div className="d-flex align-items-center">
                              <span className={`text-${benefit.color} me-2`}>
                                {benefit.icon}
                              </span>
                              <span className="fw-semibold">{benefit.label}</span>
                            </div>
                            <small className="text-muted d-block mt-1">
                              {benefit.description}
                            </small>
                          </div>
                        }
                        className="cursor-pointer"
                      />
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Amount and Description */}
            <div className="mb-4">
              <Form.Label className="fw-bold fs-6 mb-3">
                <span className="bg-success text-white px-3 py-1 rounded">Step 3</span>
                <span className="ms-2">Enter Details</span>
              </Form.Label>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      <FaMoneyBillWave className="me-2 text-success" />
                      Amount (GHS) *
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">₵</span>
                      <Form.Control
                        type="text"
                        name="amount"
                        value={formData.amount}
                        onChange={handleAmountChange}
                        placeholder="0.00"
                        required
                        className="border-start-0"
                        disabled={loading}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Enter the benefit amount in Ghana Cedis (Max: ₵100,000)
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      <FaFileAlt className="me-2 text-success" />
                      Description *
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe the reason for this benefit... (At least 10 characters)"
                      required
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      {formData.description && (
                        <span
                          className={`${formData.description.length < 10
                              ? "text-danger"
                              : "text-success"
                            }`}
                        >
                          {formData.description.length}/10 characters
                        </span>
                      )}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Benefit Summary */}
            {selectedUser && (
              <Alert variant="light" className="mb-0 border">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong className="text-success">📋 Benefit Summary</strong>
                    <div className="mt-2 small">
                      <div>
                        <span className="text-muted">Type:</span>{" "}
                        <strong>{getSelectedBenefitType().label}</strong>
                      </div>
                      <div>
                        <span className="text-muted">Amount:</span>{" "}
                        <strong className="text-success">
                          ₵{formatCurrency(formData.amount)}
                        </strong>
                      </div>
                      <div>
                        <span className="text-muted">For:</span>{" "}
                        <strong>{selectedUser.full_name}</strong>
                        {selectedDependent && (
                          <>
                            <span className="mx-1">→</span>
                            <strong className="text-success">
                              {selectedDependent.full_name}
                              {selectedDependent.relationship && (
                                <span className="text-muted ms-1">
                                  ({selectedDependent.relationship})
                                </span>
                              )}
                            </strong>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={`text-${getSelectedBenefitType().color} opacity-75`}>
                    <div style={{ fontSize: '3rem' }}>
                      {getSelectedBenefitType().icon}
                    </div>
                  </div>
                </div>
              </Alert>
            )}
          </Modal.Body>

          <Modal.Footer className="bg-light px-4 py-3">
            <Button
              variant="outline-secondary"
              onClick={onHide}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              type="submit"
              disabled={
                loading ||
                isSubmitting ||
                !formData.user_id ||
                !formData.amount ||
                !formData.description ||
                formData.description.length < 10 ||
                parseFloat(formData.amount) <= 0
              }
              className="px-4"
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <FaHandHoldingHeart className="me-2" />
                  Add Benefit
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AddBenefitModal;